import {createSlice, createAsyncThunk} from "@reduxjs/toolkit";
import {
    ErrorStatusTypes,
    ErrorsStack,
    parseErrorMessage,
} from "components/common/errorsDeclarations";

import * as anagraficheApi from "../../api/anagrafiche/anagraficheApi";

// Interfacce per User
export interface User {
    id: number;
    username: string;
    first_name: string;
    last_name: string;
    email: string;
    full_name: string;
}

// Interfacce per Commerciale
export interface Commerciale {
    id: number;
    user?: User;
    user_display?: string;
    email?: string;
    telefono?: string;
    soggetti_count?: number;
    created_at?: string;
    updated_at?: string;
}

export interface Commerciali {
    count: number;
    page: number;
    num_pages: number;
    next?: string;
    previous?: string;
    results: Commerciale[];
    errorsStack: ErrorsStack;
}

// Interfacce per Comune e Provincia (se non già definite altrove)
export interface Comune {
    id: number;
    nome: string;
    codice_catastale: string;
}

export interface Provincia {
    id: number;
    nome: string;
    codice: string;
}

// Interfacce per Mandatario
export interface Soggetto {
    id: number;
    nome: string;
    referente?: string;
    email?: string;
    telefono?: string;
    indirizzo?: string;
    cap?: string;
    comune?: Comune;
    comune_display?: string;
    provincia?: Provincia;
    provincia_display?: string;
    is_mandatario?: boolean;
    is_confezionista?: boolean;
    is_brand?: boolean;
    attivo: boolean;
    commerciale?: Commerciale;
    commerciale_display?: string;
    created_at?: string;
    updated_at?: string;
}

export interface Soggetti {
    count: number;
    page: number;
    num_pages: number;
    next?: string;
    previous?: string;
    results: Soggetto[];
    errorsStack: ErrorsStack;
}

// Interfacce per Stagione
export interface Stagione {
    id: number;
    codice: string;
    descrizione?: string;
    display_name?: string;
    created_at?: string;
    updated_at?: string;
}

export interface Stagioni {
    count: number;
    page: number;
    num_pages: number;
    next?: string;
    previous?: string;
    results: Stagione[];
    errorsStack: ErrorsStack;
}

// Interfacce per Tecnica
export interface Tecnica {
    id: number;
    nome: string;
    descrizione?: string;
    display_name?: string;
    created_at?: string;
    updated_at?: string;
}

export interface Tecniche {
    count: number;
    page: number;
    num_pages: number;
    next?: string;
    previous?: string;
    results: Tecnica[];
    errorsStack: ErrorsStack;
}

// Interfaccia per Macchina
export interface Macchina {
    id: number;
    codice: string;
    descrizione?: string;
    capacita_giornaliera?: number;
    tecniche?:number[]
    note?: string;
}

export interface Macchine {
    count: number;
    page: number;
    num_pages: number;
    next?: string;
    previous?: string;
    results: Macchina[];
    errorsStack: ErrorsStack;
}


export interface GiornoLavorativo {
    data: string;           // ISO "2025-02-10"
    lavorativo: boolean;
    motivo?: string | null; // "Weekend" | "Festività" | "Giorno non lavorativo"
}

export interface GiorniLavorativiState {
    giorni: Record<string, GiornoLavorativo>;  // chiave = data ISO
    errorsStack: ErrorsStack;
}

// Stato del slice
export interface AnagraficheState {
    commerciali: Commerciali;
    soggetti: Soggetti;
    stagioni: Stagioni;
    tecniche: Tecniche;
    macchine:Macchine;
    giorniLavorativi: GiorniLavorativiState;
}

const initialState: AnagraficheState = {
    commerciali: {
        count: 0,
        page: 0,
        num_pages: 0,
        next: undefined,
        previous: undefined,
        results: [],
        errorsStack: {status: ErrorStatusTypes.OK},
    },
    soggetti: {
        count: 0,
        page: 0,
        num_pages: 0,
        next: undefined,
        previous: undefined,
        results: [],
        errorsStack: {status: ErrorStatusTypes.OK},
    },
    stagioni: {
        count: 0,
        page: 0,
        num_pages: 0,
        next: undefined,
        previous: undefined,
        results: [],
        errorsStack: {status: ErrorStatusTypes.OK},
    },
    tecniche: {
        count: 0,
        page: 0,
        num_pages: 0,
        next: undefined,
        previous: undefined,
        results: [],
        errorsStack: {status: ErrorStatusTypes.OK},
    },
    macchine: {
        count: 0,
        page: 0,
        num_pages: 0,
        next: undefined,
        previous: undefined,
        results: [],
        errorsStack: {status: ErrorStatusTypes.OK},
    },
    giorniLavorativi: {
        giorni: {},
        errorsStack: { status: ErrorStatusTypes.OK },
    },
};


// AsyncThunk actions
export const fetchCommerciali = createAsyncThunk(
    "anagrafiche/fetchCommerciali",
    async () => {
        return await anagraficheApi.fetchCommerciali();
    }
);

export const fetchMandatari = createAsyncThunk(
    "anagrafiche/fetchMandatari",
    async () => {
        return await anagraficheApi.fetchSoggetti();
    }
);

export const fetchStagioni = createAsyncThunk(
    "anagrafiche/fetchStagioni",
    async () => {
        return await anagraficheApi.fetchStagioni();
    }
);

export const fetchTecniche = createAsyncThunk(
    "anagrafiche/fetchTecniche",
    async () => {
        return await anagraficheApi.fetchTecniche();
    }
);
export const fetchMacchine = createAsyncThunk(
    "anagrafiche/fetchMacchine",
    async () => {
        return await anagraficheApi.fetchMacchine();
    }
);

export const fetchGiorniLavorativi = createAsyncThunk(
    "anagrafiche/fetchGiorniLavorativi",
    async (parametri: { data_da: string; data_a: string }) => {
        return await anagraficheApi.fetchGiorniLavorativi(
            parametri.data_da,
            parametri.data_a
        );
    }
);

// Slice
export const anagraficheSlice = createSlice({
    name: "anagrafiche",
    initialState,
    reducers: {
        resetAnagrafiche: (state) => {
            state.commerciali = initialState.commerciali;
            state.soggetti = initialState.soggetti;
            state.stagioni = initialState.stagioni;
            state.tecniche = initialState.tecniche;
            state.giorniLavorativi = initialState.giorniLavorativi;
        },
        resetGiorniLavorativi: (state) => {
            state.giorniLavorativi = initialState.giorniLavorativi;
        },
    },
    extraReducers: (builder) => {
        // fetch Commerciali
        builder.addCase(fetchCommerciali.pending, (state) => {
            if (!state.commerciali) {
                state.commerciali = initialState.commerciali;
            } else{
                state.commerciali.errorsStack = {status: ErrorStatusTypes.PENDING};
            }
        });
        builder.addCase(fetchCommerciali.fulfilled, (state, action) => {
            state.commerciali = action.payload;
            state.commerciali.errorsStack = {status: ErrorStatusTypes.OK};
        });
        builder.addCase(fetchCommerciali.rejected, (state, action) => {
            if (!state.commerciali) {
                state.commerciali = initialState.commerciali;
            } else{
                state.commerciali.errorsStack = parseErrorMessage(action.error);
            }
        });

        // fetch Mandatari
        builder.addCase(fetchMandatari.pending, (state) => {
            state.soggetti.errorsStack = {status: ErrorStatusTypes.PENDING};
        });
        builder.addCase(fetchMandatari.fulfilled, (state, action) => {
            state.soggetti = action.payload;
            state.soggetti.errorsStack = {status: ErrorStatusTypes.OK};
        });
        builder.addCase(fetchMandatari.rejected, (state, action) => {
            if (!state.soggetti) {
                state.soggetti = initialState.soggetti;
            } else{
                state.soggetti.errorsStack = parseErrorMessage(action.error);
            }
        });

        // fetch Stagioni
        builder.addCase(fetchStagioni.pending, (state) => {
            state.stagioni.errorsStack = {status: ErrorStatusTypes.PENDING};
        });
        builder.addCase(fetchStagioni.fulfilled, (state, action) => {
            state.stagioni = action.payload;
            state.stagioni.errorsStack = {status: ErrorStatusTypes.OK};
        });
        builder.addCase(fetchStagioni.rejected, (state, action) => {
            if (!state.stagioni) {
                state.stagioni = initialState.stagioni;
            } else {
                state.stagioni.errorsStack = parseErrorMessage(action.error);
            }
        });

        // fetch Tecniche
        builder.addCase(fetchTecniche.pending, (state) => {
            state.tecniche.errorsStack = {status: ErrorStatusTypes.PENDING};
        });
        builder.addCase(fetchTecniche.fulfilled, (state, action) => {
            state.tecniche = action.payload;
            state.tecniche.errorsStack = {status: ErrorStatusTypes.OK};
        });
        builder.addCase(fetchTecniche.rejected, (state, action) => {
            if (!state.tecniche) {
                state.tecniche = initialState.tecniche;
            } else {
                state.tecniche.errorsStack = parseErrorMessage(action.error);
            }
        });

        builder.addCase(fetchMacchine.pending, (state) => {
            state.macchine.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(fetchMacchine.fulfilled, (state, action) => {
            state.macchine = action.payload;
            state.macchine.errorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(fetchMacchine.rejected, (state, action) => {
            if (!state.macchine) {
                state.macchine = initialState.macchine;
            } else {
                state.macchine.errorsStack = parseErrorMessage(action.error);
            }
        });

        // fetch Giorni Lavorativi
        builder.addCase(fetchGiorniLavorativi.pending, (state) => {
            state.giorniLavorativi.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(fetchGiorniLavorativi.fulfilled, (state, action) => {
            // Merge: i nuovi giorni si aggiungono/sovrascrivono ai già presenti
            const giorni = action.payload.giorni as GiornoLavorativo[];
            giorni.forEach((giorno) => {
                state.giorniLavorativi.giorni[giorno.data] = giorno;
            });
            state.giorniLavorativi.errorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(fetchGiorniLavorativi.rejected, (state, action) => {
            state.giorniLavorativi.errorsStack = parseErrorMessage(action.error);
        });
    },
});

// Action creators
export const {resetAnagrafiche, resetGiorniLavorativi} = anagraficheSlice.actions;

export const anagraficheSliceReducer = anagraficheSlice.reducer;